const katakanaToRomanjiMap: Record<string, string> = {
    // Basic vowels
    "ア": "a", "イ": "i", "ウ": "u", "エ": "e", "オ": "o",
    // K-row
    "カ": "ka", "キ": "ki", "ク": "ku", "ケ": "ke", "コ": "ko",
    // S-row (Hepburn: "シ" = "shi" instead of "si")
    "サ": "sa", "シ": "shi", "ス": "su", "セ": "se", "ソ": "so",
    // T-row (Hepburn: "チ" = "chi", "ツ" = "tsu")
    "タ": "ta", "チ": "chi", "ツ": "tsu", "テ": "te", "ト": "to",
    // N-row
    "ナ": "na", "ニ": "ni", "ヌ": "nu", "ネ": "ne", "ノ": "no",
    // H-row (Hepburn: "フ" = "fu" instead of "hu")
    "ハ": "ha", "ヒ": "hi", "フ": "fu", "ヘ": "he", "ホ": "ho",
    // M-row
    "マ": "ma", "ミ": "mi", "ム": "mu", "メ": "me", "モ": "mo",
    // Y-row
    "ヤ": "ya", "ユ": "yu", "ヨ": "yo",
    // R-row
    "ラ": "ra", "リ": "ri", "ル": "ru", "レ": "re", "ロ": "ro",
    // W-row
    "ワ": "wa", "ヲ": "o", "ン": "n",
    // Dakuten sounds (voiced consonants)
    "ガ": "ga", "ギ": "gi", "グ": "gu", "ゲ": "ge", "ゴ": "go",
    "ザ": "za", "ジ": "ji", "ズ": "zu", "ゼ": "ze", "ゾ": "zo",
    "ダ": "da", "ヂ": "ji", "ヅ": "zu", "デ": "de", "ド": "do",
    "バ": "ba", "ビ": "bi", "ブ": "bu", "ベ": "be", "ボ": "bo",
    // Handakuten sounds (p-sound)
    "パ": "pa", "ピ": "pi", "プ": "pu", "ペ": "pe", "ポ": "po",
    // Small kana combinations (e.g., "キャ" = "kya")
    "キャ": "kya", "キュ": "kyu", "キョ": "kyo",
    "シャ": "sha", "シュ": "shu", "ショ": "sho",
    "チャ": "cha", "チュ": "chu", "チョ": "cho",
    "ニャ": "nya", "ニュ": "nyu", "ニョ": "nyo",
    "ヒャ": "hya", "ヒュ": "hyu", "ヒョ": "hyo",
    "ミャ": "mya", "ミュ": "myu", "ミョ": "myo",
    "リャ": "rya", "リュ": "ryu", "リョ": "ryo",
    "ギャ": "gya", "ギュ": "gyu", "ギョ": "gyo",
    "ジャ": "ja", "ジュ": "ju", "ジョ": "jo",
    "ビャ": "bya", "ビュ": "byu", "ビョ": "byo",
    "ピャ": "pya", "ピュ": "pyu", "ピョ": "pyo",
    // Special cases
    "ッ": "", // Small "ッ" doubles the next consonant
    "ー": "" // Prolonged sound marker
};

// Mapping Half-width Katakana (1-byte) to Full-width Katakana (2-byte)
const halfWidthToFullWidth: Record<string, string> = {
    "ｱ": "ア", "ｲ": "イ", "ｳ": "ウ", "ｴ": "エ", "ｵ": "オ",
    "ｶ": "カ", "ｷ": "キ", "ｸ": "ク", "ｹ": "ケ", "ｺ": "コ",
    "ｻ": "サ", "ｼ": "シ", "ｽ": "ス", "ｾ": "セ", "ｿ": "ソ",
    "ﾀ": "タ", "ﾁ": "チ", "ﾂ": "ツ", "ﾃ": "テ", "ﾄ": "ト",
    "ﾅ": "ナ", "ﾆ": "ニ", "ﾇ": "ヌ", "ﾈ": "ネ", "ﾉ": "ノ",
    "ﾊ": "ハ", "ﾋ": "ヒ", "ﾌ": "フ", "ﾍ": "ヘ", "ﾎ": "ホ",
    "ﾏ": "マ", "ﾐ": "ミ", "ﾑ": "ム", "ﾒ": "メ", "ﾓ": "モ",
    "ﾔ": "ヤ", "ﾕ": "ユ", "ﾖ": "ヨ",
    "ﾗ": "ラ", "ﾘ": "リ", "ﾙ": "ル", "ﾚ": "レ", "ﾛ": "ロ",
    "ﾜ": "ワ", "ｦ": "ヲ", "ﾝ": "ン",
    "ｧ": "ァ", "ｨ": "ィ", "ｩ": "ゥ", "ｪ": "ェ", "ｫ": "ォ",
    "ｬ": "ャ", "ｭ": "ュ", "ｮ": "ョ", "ｯ": "ッ",
    "ｰ": "ー" // Prolonged sound marker
};

// Dakuten & Handakuten mappings
const dakutenMap: Record<string, string> = {
    "ｶﾞ": "ガ", "ｷﾞ": "ギ", "ｸﾞ": "グ", "ｹﾞ": "ゲ", "ｺﾞ": "ゴ",
    "ｻﾞ": "ザ", "ｼﾞ": "ジ", "ｽﾞ": "ズ", "ｾﾞ": "ゼ", "ｿﾞ": "ゾ",
    "ﾀﾞ": "ダ", "ﾁﾞ": "ヂ", "ﾂﾞ": "ヅ", "ﾃﾞ": "デ", "ﾄﾞ": "ド",
    "ﾊﾞ": "バ", "ﾋﾞ": "ビ", "ﾌﾞ": "ブ", "ﾍﾞ": "ベ", "ﾎﾞ": "ボ"
};
const handakutenMap: Record<string, string> = {
    "ﾊﾟ": "パ", "ﾋﾟ": "ピ", "ﾌﾟ": "プ", "ﾍﾟ": "ペ", "ﾎﾟ": "ポ"
};

// Convert Half-width Katakana to Full-width Katakana (including dakuten & handakuten)
export function convertHalfWidthToFullWidth(input: string) {
    let result = "";
    for (let i = 0; i < input.length; i++) {
        let char = input[i];
        let nextChar = input[i + 1];

        // Check for dakuten (ﾞ) and handakuten (ﾟ)
        if (nextChar === "ﾞ" || nextChar === "ﾟ") {
            let combined = char + nextChar;
            if (dakutenMap[combined]) {
                result += dakutenMap[combined];
                i++; // Skip next character
                continue;
            } else if (handakutenMap[combined]) {
                result += handakutenMap[combined];
                i++; // Skip next character
                continue;
            }
        }

        // Normal half-width to full-width conversion
        result += halfWidthToFullWidth[char] || char;
    }
    return result;
}

// Main function to convert Katakana (Half-width or Full-width) to Hepburn Romanization
export function katakanaToHepburn(katakana: string) {
    // Convert half-width Katakana to full-width
    katakana = convertHalfWidthToFullWidth(katakana);

    let result = "";
    
    for (let i = 0; i < katakana.length; i++) {
        let char = katakana[i];

        // Handle small kana combinations (e.g., "キャ" -> "kya")
        if (i < katakana.length - 1) {
            let nextChar = katakana[i + 1];
            let combined = char + nextChar;
            if (katakanaToRomanjiMap[combined]) {
                result += katakanaToRomanjiMap[combined];
                i++; // Skip the next character
                continue;
            }
        }

        // Handle small "ッ" (sokuon) which doubles the next consonant
        if (char === "ッ" && i < katakana.length - 1) {
            let nextRomanji = katakanaToRomanjiMap[katakana[i + 1]];
            if (nextRomanji) {
                result += nextRomanji[0]; // Double the first consonant
            }
            continue;
        }

        // Handle "ー" (prolonged sound marker)
        if (char === "ー" && result.length > 0) {
            result += result[result.length - 1]; // Repeat the last vowel
            continue;
        }

        // Convert regular Katakana characters
        result += katakanaToRomanjiMap[char] || char;
    }

    // Hepburn rule: "ン" before 'b', 'm', 'p' becomes "m"
    result = result.replace(/n([bmp])/g, "m$1");
    
    return result;
}


//#region Kuromoji
import { LRUCache } from 'lru-cache';
// Chuyển đổi giá trị sang Hiragana và lưu trữ vào map
const hiraganaCache = new LRUCache<string, string>({ 
  max: 100000, 
  ttl: 1000 * 60 * 30 //30 min
});

// Chuyển đổi văn bản Kanji sang Hiragana
export function convertToHiragana(kanjiText: string, tokenizer: any): string {
  if(!kanjiText) return "";
  if(!tokenizer || !tokenizer.tokenize) return kanjiText;

  const normalized = normalizeString(kanjiText);
  const tokens = tokenizer.tokenize(normalized);

  let hiragana = '';
  tokens.forEach((token: any) => {
    if (token.reading) {
      // Chuyển Katakana thành Hiragana
      hiragana += katakanaToHiragana(token.reading);
    } else {
      // Giữ nguyên surface nếu không có cách đọc
      hiragana += token.surface_form;
    }
  });
  return hiragana;
}
  
// Chuyển Katakana thành Hiragana
function katakanaToHiragana(katakanaText: string): string {
  return katakanaText.replace(/[\u30a1-\u30f6]/g, (char) =>
    String.fromCharCode(char.charCodeAt(0) - 0x60)
  );
}

function normalizeString(input: string): string {
  // Chuyển số full-width sang half-width
  const fullWidthToHalfWidth = (str: string) =>
    str.replace(/[\uFF01-\uFF5E]/g, (char) =>
      String.fromCharCode(char.charCodeAt(0) - 0xFEE0)
    ).replace(/\u3000/g, ' '); // Thay khoảng trắng full-width thành khoảng trắng thường

  const normalized = fullWidthToHalfWidth(input);
  return normalized;
}
  
export function sortData(
  list: any[],
  tokenizer: any,
  order: 'asc' | 'desc' = 'asc',
  key?: string,
): any[] {

  // Sắp xếp danh sách
  return list.sort((a, b) => {
    const aValue =
      typeof a === 'string'
        ? a
        : key && typeof a[key] === 'string'
        ? a[key]
        : '';
    const bValue =
      typeof b === 'string'
        ? b
        : key && typeof b[key] === 'string'
        ? b[key]
        : '';

    let aHiragana = hiraganaCache.get(aValue);
    if (!aHiragana && aValue) {
      aHiragana = convertToHiragana(aValue, tokenizer); 
      hiraganaCache.set(aValue, aHiragana);
    }

    let bHiragana = hiraganaCache.get(bValue);
    if (!bHiragana && bValue) {
      bHiragana = convertToHiragana(bValue, tokenizer);
      hiraganaCache.set(bValue, bHiragana);
    }

    aHiragana = aHiragana ?? '';
    bHiragana = bHiragana ?? '';

    const comparison = aHiragana.localeCompare(bHiragana, 'ja');
    return order === 'asc' ? comparison : -comparison;
  });
}
  
  // Hàm xây dựng tokenizer for sorting
let cachedTokenizer: any | null = null;
declare const kuromoji: any;

export function buildTokenizer(): Promise<any> {
  if (cachedTokenizer) {
    return Promise.resolve(cachedTokenizer);
  }

  return new Promise((resolve, reject) => {
    kuromoji.builder({ dicPath: '/assets/dict/'}).build((err: any, tokenizer: any) => {
      if (err) {
        reject(err);
      } else {
        cachedTokenizer = tokenizer;
        resolve(tokenizer);
      }
    });
  });
}
//#endregion Kuromoji